import { Button, Card, Group, Stack, Text, ThemeIcon } from "@mantine/core"
import MicrosoftAuthenticatorLogo from "@assets/microsoft_authenticator_logo.png"
import { useAuthenticationMethods } from "../queries"
import { IconCheck } from "@tabler/icons-react"
import { useMemo } from "react"
import { useQueryClient } from "@tanstack/react-query"

const StepNumber: React.FC<{ step: number }> = ({ step }) => {
    return (
        <ThemeIcon>
            <Text size="md" fw={700} c="white">
                {step}
            </Text>
        </ThemeIcon>
    )
}

const Step: React.FC<{ step: number; body: React.ReactNode }> = ({ step, body }) => {
    return (
        <Group gap={"lg"} wrap="nowrap">
            <StepNumber step={step} />
            <Stack>
                {body}
            </Stack>
        </Group>
    )
}

export const AuthenticatorStep: React.FC = () => {

    const { data: authenticationMethods, isFetching } = useAuthenticationMethods()

    const has2fa = useMemo(() => authenticationMethods && authenticationMethods?.length > 1, [authenticationMethods])

    const queryClient = useQueryClient()

    return (
        <Stack w={700}>
            <Text>
                When you're on campus, your username and password is all you need to log in. When logging in from elsewhere (e.g. home), an extra security check is needed to make sure it's really you. This is called Two-Factor-Authentication (2FA). Follows these instructions to set it up.
            </Text>
            <Stack ml={'xl'} w={600} mt="lg">
                <Step step={1} body={<Group><Text size="sm">Download the Microsoft Authenticator app from the app store on your phone.</Text><Card withBorder p={0}><img src={MicrosoftAuthenticatorLogo} alt="Microsoft Authenticator Logo" style={{ height: 30 }}/></Card></Group>} />
                <Step step={2} body={<Text size="sm">Read through these instructions, then click 'Open authentication site' to open the site in a new tab.</Text>} />
                <Step step={3} body={<Text size="sm">Once you have opened the authentication site, click <Text span fw={700} fs="italic">Add sign-in method</Text> and choose <Text span fw={700} fs="italic">Authenticator app</Text>.</Text>} />
                <Step step={4} body={<Text size="sm">Follow the instructions on the site and return here when you are done.</Text>} />
                <Step step={5} body={<Text size="sm">Click 'Check 2FA' to ensure it's set up correctly.</Text>} />
            </Stack>
            <Stack mt="xl" align="center">
                <Button className="w-fit!" component="a" href="https://mysignins.microsoft.com/security-info" target="_blank" rel="noopener noreferrer">
                    Open authentication site
                </Button>
                <Button 
                    loading={isFetching} 
                    leftSection={has2fa ? <IconCheck size={20} stroke={2.5} /> : null} 
                    color={has2fa ? "green" : "blue"} 
                    className="w-fit!" 
                    onClick={() => queryClient.invalidateQueries({ queryKey: ['authenticationMethods'] })}
                >
                    {has2fa ? "2FA is set up" : "Check 2FA"}
                </Button>
            </Stack>
        </Stack>
    )
}